import { useEffect, useRef } from "react";

/**
 * Invoke `callback` on a pointer press outside `ref`.
 *
 * The listener runs in the capture phase. A parent that stops `click` from
 * bubbling — Headless UI dialog panels do this — never lets a bubble-phase
 * document listener see the press, so the menu would stay open.
 *
 * @param {{ current: HTMLElement | null }} ref Element that counts as inside.
 * @param {(event: PointerEvent) => void} callback Called for an outside press.
 */
const useOutsideClick = (ref, callback) => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    function handlePointerDown(event) {
      const node = ref.current;
      if (!node || node.contains(event.target)) return;
      callbackRef.current(event);
    }

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [ref]);
};

export default useOutsideClick;
